import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { COLORS } from "../theme/theme-options";

type WizardStepperProps = {
  steps: readonly { title: string }[];
  activeStep: number;
  onStepChange?: (next: number) => void;
};

export const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  activeStep,
  onStepChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const stepRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const centerActiveStep = React.useCallback(() => {
    const container = containerRef.current;
    const step = stepRefs.current[activeStep];

    if (!container || !step) {
      return;
    }

    const containerWidth = container.clientWidth;
    const maxScrollLeft = container.scrollWidth - containerWidth;
    const idealScrollLeft =
      step.offsetLeft - containerWidth / 2 + step.offsetWidth / 2;
    const nextScrollLeft = Math.max(0, Math.min(idealScrollLeft, maxScrollLeft));

    container.scrollTo({
      left: nextScrollLeft,
      behavior: "smooth",
    });
  }, [activeStep]);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      centerActiveStep();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeStep, centerActiveStep, steps.length]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        pb: 0.5,
        scrollbarWidth: "thin",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ width: "max-content", minWidth: "100%" }}>
        {steps.map(({ title }, index) => {
          const isActive = index === activeStep;
          const isClickable = typeof onStepChange === "function";
          const stepNumber = index + 1;

          return (
            <React.Fragment key={`step-${index}`}>
              <Box
                component="button"
                type="button"
                ref={(element: HTMLButtonElement | null) => {
                  stepRefs.current[index] = element;
                }}
                onClick={() => {
                  if (onStepChange) {
                    onStepChange(index);
                  }
                }}
                aria-current={isActive ? "step" : undefined}
                disabled={!isClickable}
                sx={{
                  all: "unset",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  border: isActive
                    ? "1px solid rgba(34, 211, 238, 0.65)"
                    : "1px solid transparent",
                  bgcolor: isActive
                    ? "rgba(34, 211, 238, 0.08)"
                    : "transparent",
                  cursor: isClickable ? "pointer" : "default",
                  userSelect: "none",
                  transition:
                    "background-color 120ms ease, border-color 120ms ease",
                  "&:hover": isClickable
                    ? {
                        bgcolor: isActive
                          ? "rgba(34, 211, 238, 0.10)"
                          : "rgba(148, 163, 184, 0.06)",
                      }
                    : undefined,
                  "&:focus-visible": {
                    outline: "2px solid rgba(34, 211, 238, 0.55)",
                    outlineOffset: 2,
                  },
                  "&:disabled": {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: isActive
                      ? "rgb(34, 211, 238)"
                      : "rgba(15, 23, 42, 0.35)",
                    border: isActive
                      ? "none"
                      : "1px solid rgba(148, 163, 184, 0.30)",
                    color: isActive ? "#07121f" : "rgba(226, 232, 240, 0.75)",
                  }}
                >
                  {stepNumber}
                </Box>

                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: isActive ? 700 : 600,
                    color: isActive
                      ? "rgb(34, 211, 238)"
                      : activeStep < index
                        ? "rgba(148, 163, 184, 0.55)"
                        : COLORS.grey[100],
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </Typography>
              </Box>

              {index < steps.length - 1 && (
                <Box
                  sx={{
                    px: 1.25,
                    display: "flex",
                    alignItems: "center",
                    color: "rgba(148, 163, 184, 0.55)",
                  }}
                >
                  <ChevronRightRoundedIcon fontSize="small" />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};
